<template>
  <div class="slider-view">
    <header class="slider-view__header">
      <div class="slider-view__header-top">
        <h2 class="slider-view__title">Slider</h2>
        <button
          type="button"
          class="slider-view__btn-save"
          @click="saveAll"
        >
          Save
        </button>
      </div>
    </header>

    <div class="slider-view__body">
      <div class="slider-view__grid">
        <div
          v-for="(slide, index) in editSlides"
          :key="`slide-${index}`"
          class="slider-view__card"
          :class="{ 'slider-view__card--dragging': imageDraggedIndex === index }"
          @dragover.prevent="onImageDragOver($event, index)"
          @drop="onImageDrop(index)"
        >
          <div class="slider-view__card-img-wrap">
            <span
              class="slider-view__drag-handle"
              draggable="true"
              aria-label="Drag to reorder"
              @dragstart="onImageDragStart($event, index, $event.currentTarget.closest('.slider-view__card'))"
              @dragend="onImageDragEnd"
              @click.stop
            >
              <span class="slider-view__drag-lines">
                <span class="slider-view__drag-line"></span>
                <span class="slider-view__drag-line"></span>
              </span>
            </span>
            <div class="slider-view__thumb-click" @click="openLightbox(index)">
              <img :src="imageDisplayUrl(slide.url)" class="slider-view__img" alt="">
            </div>
            <button
              type="button"
              class="slider-view__thumb-remove"
              aria-label="Remove image"
              @click.stop="removeImage(index)"
            >
              <span class="material-symbols-outlined slider-view__thumb-remove-icon">delete</span>
            </button>
          </div>
          <div class="slider-view__card-fields">
            <el-input
              :value="slide.descriptionRs || ''"
              placeholder="Description (Serbian)"
              type="textarea"
              :autosize="{ minRows: 2 }"
              class="slider-view__card-input"
              @input="(val) => updateSlideField(index, 'descriptionRs', val)"
            />
            <el-input
              :value="slide.descriptionEn || ''"
              placeholder="Description (English)"
              type="textarea"
              :autosize="{ minRows: 2 }"
              class="slider-view__card-input"
              @input="(val) => updateSlideField(index, 'descriptionEn', val)"
            />
          </div>
        </div>
        <button
          type="button"
          class="slider-view__add-slot"
          aria-label="Add images"
          @click="$refs.uploadFileInput && $refs.uploadFileInput.click()"
        >
          <span class="material-symbols-outlined slider-view__add-slot-icon">add</span>
          <span class="slider-view__add-slot-text">+ Add image</span>
        </button>
      </div>
      <input
        ref="uploadFileInput"
        type="file"
        accept="image/*"
        multiple
        class="slider-view__file-input"
        @change="onUploadFilesChange"
      >
      <p v-if="!editSlides.length" class="slider-view__empty">
        No images yet. Click + Add image to add slides with descriptions.
      </p>
    </div>

    <div
      v-if="lightboxVisible && editSlides.length && editSlides[lightboxIndex]"
      ref="lightbox"
      tabindex="-1"
      class="slider-view__lightbox"
      @keydown.esc="closeLightbox"
      @keydown.left.prevent="goToPrevImage"
      @keydown.right.prevent="goToNextImage"
    >
      <button
        type="button"
        class="slider-view__lightbox-close"
        aria-label="Close"
        @click="closeLightbox"
      >
        <span class="material-symbols-outlined">close</span>
      </button>
      <button
        v-if="editSlides.length > 1"
        type="button"
        class="slider-view__lightbox-prev"
        aria-label="Previous image"
        @click="goToPrevImage"
      >
        <span class="material-symbols-outlined">chevron_left</span>
      </button>
      <img
        :src="imageDisplayUrl(editSlides[lightboxIndex].url)"
        class="slider-view__lightbox-img"
        alt=""
        @click.stop
      >
      <button
        v-if="editSlides.length > 1"
        type="button"
        class="slider-view__lightbox-next"
        aria-label="Next image"
        @click="goToNextImage"
      >
        <span class="material-symbols-outlined">chevron_right</span>
      </button>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { Message, Input } from 'element-ui'

export default {
  name: 'Slider',

  components: {
    ElInput: Input
  },

  data () {
    return {
      editSlides: [],
      imageDraggedIndex: null,
      lightboxVisible: false,
      lightboxIndex: 0,
      dragPreviewEl: null
    }
  },

  watch: {
    slides: {
      handler (val) {
        this.editSlides = Array.isArray(val)
          ? val.map((s) => ({
            url: s.url,
            descriptionRs: s.descriptionRs ?? s.description ?? '',
            descriptionEn: s.descriptionEn ?? ''
          }))
          : []
      },
      immediate: true
    }
  },

  computed: {
    ...mapGetters('slider', ['slides'])
  },

  mounted () {
    this.load()
  },

  methods: {
    ...mapActions('slider', ['load', 'update']),
    ...mapActions('galerija', ['uploadGalleryImage']),

    imageDisplayUrl (url) {
      if (!url) return ''
      return url.startsWith('http') || url.startsWith('data:') ? url : (this.$path + (url.startsWith('/') ? url : '/' + url))
    },

    async onUploadFilesChange (e) {
      const files = Array.from(e.target.files || [])
      if (!files.length) return
      if (this.$refs.uploadFileInput) this.$refs.uploadFileInput.value = ''
      try {
        const paths = await Promise.all(
          files.map((file) => this.uploadGalleryImage(file))
        )
        paths.forEach((path) => {
          this.editSlides.push({ url: path, descriptionRs: '', descriptionEn: '' })
        })
        Message.success('Images added. Click Save to persist.')
      } catch {
        Message.error('Upload failed.')
      }
    },

    onImageDragStart (e, index, thumbElement) {
      this.imageDraggedIndex = index
      e.dataTransfer.effectAllowed = 'move'
      e.dataTransfer.setData('text/plain', String(index))
      if (thumbElement) {
        const imgWrap = thumbElement.querySelector('.slider-view__card-img-wrap')
        const img = imgWrap && imgWrap.querySelector('.slider-view__img')
        if (imgWrap && img) {
          const rect = imgWrap.getBoundingClientRect()
          const ghost = document.createElement('div')
          ghost.setAttribute('class', 'slider-view__drag-ghost')
          ghost.style.cssText = `position:fixed;left:${e.clientX}px;top:${e.clientY}px;width:${rect.width}px;height:${rect.height}px;transform:translate(-50%,-50%);overflow:hidden;background:#f1f5f9;pointer-events:none;z-index:9999;box-shadow:0 8px 24px rgba(0,0,0,0.15);border-radius:0.5rem;`
          const imgClone = img.cloneNode(true)
          imgClone.style.cssText = 'width:100%;height:100%;object-fit:cover;display:block;'
          ghost.appendChild(imgClone)
          document.body.appendChild(ghost)
          this.dragPreviewEl = ghost
          this._boundUpdateDragGhost = (ev) => this.updateDragGhostPosition(ev)
          document.addEventListener('dragover', this._boundUpdateDragGhost)
          const transparent = document.createElement('div')
          transparent.setAttribute('aria-hidden', 'true')
          transparent.style.cssText = 'position:fixed;left:-9999px;width:1px;height:1px;opacity:0;pointer-events:none;'
          document.body.appendChild(transparent)
          this._transparentDragImage = transparent
          e.dataTransfer.setDragImage(transparent, 0, 0)
        } else {
          const rect = thumbElement.getBoundingClientRect()
          e.dataTransfer.setDragImage(thumbElement, rect.width / 2, rect.height / 2)
        }
      }
    },

    updateDragGhostPosition (e) {
      if (this.dragPreviewEl) {
        this.dragPreviewEl.style.left = `${e.clientX}px`
        this.dragPreviewEl.style.top = `${e.clientY}px`
      }
    },

    onImageDragEnd () {
      if (this._boundUpdateDragGhost) {
        document.removeEventListener('dragover', this._boundUpdateDragGhost)
        this._boundUpdateDragGhost = null
      }
      if (this._transparentDragImage && this._transparentDragImage.parentNode) {
        this._transparentDragImage.parentNode.removeChild(this._transparentDragImage)
        this._transparentDragImage = null
      }
      if (this.dragPreviewEl && this.dragPreviewEl.parentNode) {
        this.dragPreviewEl.parentNode.removeChild(this.dragPreviewEl)
        this.dragPreviewEl = null
      }
      this.imageDraggedIndex = null
    },

    onImageDragOver (e, index) {
      if (this.imageDraggedIndex === null) return
      e.dataTransfer.dropEffect = 'move'
    },

    onImageDrop (dropIndex) {
      if (this.imageDraggedIndex === null || this.imageDraggedIndex === dropIndex) return
      const item = this.editSlides.splice(this.imageDraggedIndex, 1)[0]
      this.editSlides.splice(dropIndex, 0, item)
      this.imageDraggedIndex = null
    },

    async saveAll () {
      try {
        await this.update([...this.editSlides])
        Message.success('Slider saved.')
      } catch {
        Message.error('Failed to save slider.')
      }
    },

    updateSlideField (index, field, value) {
      if (index < 0 || index >= this.editSlides.length) return
      this.$set(this.editSlides, index, { ...this.editSlides[index], [field]: value })
    },

    removeImage (index) {
      if (index < 0 || index >= this.editSlides.length) return
      this.editSlides.splice(index, 1)
      Message.success('Image removed. Click Save to persist.')
    },

    openLightbox (index) {
      this.lightboxIndex = index
      this.lightboxVisible = true
      this.$nextTick(() => {
        if (this.$refs.lightbox) this.$refs.lightbox.focus()
      })
    },

    closeLightbox () {
      this.lightboxVisible = false
    },

    goToPrevImage () {
      if (!this.editSlides.length) return
      this.lightboxIndex = this.lightboxIndex <= 0 ? this.editSlides.length - 1 : this.lightboxIndex - 1
    },

    goToNextImage () {
      if (!this.editSlides.length) return
      this.lightboxIndex = this.lightboxIndex >= this.editSlides.length - 1 ? 0 : this.lightboxIndex + 1
    }
  }
}
</script>

<style lang="scss" scoped>
.slider-view {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  font-family: 'Manrope', sans-serif;
}

.slider-view__header {
  padding: 2rem 3rem;
  flex-shrink: 0;
  border-bottom: 1px solid #f1f5f9;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(24px);
}

.slider-view__header-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.slider-view__title {
  font-size: 2.25rem;
  font-weight: 800;
  letter-spacing: -0.025em;
  color: #0f172a;
  margin: 0;
}

.slider-view__btn-save {
  padding: 0.75rem 1.5rem;
  background: #1a1a1a;
  color: #fff;
  font-size: 0.875rem;
  font-weight: 600;
  border-radius: 9999px;
  border: none;
  cursor: pointer;
  transition: background 0.15s;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  &:hover {
    background: #334155;
  }
}

.slider-view__file-input {
  position: absolute;
  width: 0;
  height: 0;
  opacity: 0;
  pointer-events: none;
}

.slider-view__body {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 2rem 3rem;
}

.slider-view__empty {
  color: #64748b;
  font-size: 0.9375rem;
  margin: 1rem 0 0;
}

.slider-view__grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
}

.slider-view__card {
  display: flex;
  flex-direction: column;
  border-radius: 1rem;
  overflow: hidden;
  background: #fff;
  border: 1px solid #f1f5f9;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  &.slider-view__card--dragging {
    opacity: 0.6;
  }
}

.slider-view__card-img-wrap {
  position: relative;
  aspect-ratio: 1;
  overflow: hidden;
  background: #f1f5f9;
  &:hover .slider-view__thumb-remove {
    opacity: 1;
  }
}

.slider-view__card-fields {
  padding: 0.75rem;
}

.slider-view__card-input {
  :deep(.el-textarea__inner) {
    border-radius: 0.75rem;
    border: 1px solid #e2e8f0;
  }
}

.slider-view__add-slot {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-height: 12rem;
  aspect-ratio: 1;
  padding: 0;
  color: #94a3b8;
  background: #fff;
  border: 1px solid #bfdbfe;
  border-radius: 1rem;
  cursor: pointer;
  transition: color 0.15s, border-color 0.15s, background 0.15s;
  .slider-view__add-slot-icon {
    font-size: 2.5rem;
  }
  .slider-view__add-slot-text {
    font-size: 0.875rem;
    font-weight: 500;
  }
  &:hover {
    color: #64748b;
    background: #f8fafc;
    border-color: #93c5fd;
  }
}

.slider-view__drag-handle {
  position: absolute;
  top: 0.5rem;
  left: 0.5rem;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  padding: 0;
  background: rgba(255, 255, 255, 0.7);
  border: none;
  border-radius: 50%;
  cursor: grab;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
  &:active {
    cursor: grabbing;
  }
  &:hover {
    background: rgba(255, 255, 255, 0.85);
  }
}

.slider-view__drag-lines {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.1875rem;
  width: 100%;
  height: 100%;
}

.slider-view__drag-line {
  width: 0.375rem;
  height: 0.125rem;
  background: #0f172a;
  border-radius: 0.0625rem;
}

.slider-view__thumb-click {
  position: absolute;
  inset: 0;
  cursor: pointer;
  display: block;
}

.slider-view__thumb-remove {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  z-index: 2;
  width: 1.5rem;
  height: 1.5rem;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #e05452;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.15s;
  &:hover {
    background: #c94a48;
  }
  .slider-view__thumb-remove-icon {
    font-size: 1rem;
    color: #fff;
  }
}

.slider-view__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  pointer-events: none;
}

.slider-view__lightbox {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(15, 23, 42, 0.92);
  outline: none;
  &:focus {
    outline: none;
  }
}

.slider-view__lightbox-close {
  position: absolute;
  top: 1.5rem;
  right: 1.5rem;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  padding: 0;
  background: transparent;
  border: none;
  border-radius: 0.5rem;
  color: #fff;
  cursor: pointer;
  transition: background 0.15s;
  .material-symbols-outlined {
    font-size: 1.5rem;
  }
  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }
}

.slider-view__lightbox-prev,
.slider-view__lightbox-next {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 3rem;
  height: 3rem;
  padding: 0;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 50%;
  color: #fff;
  cursor: pointer;
  transition: background 0.15s;
  .material-symbols-outlined {
    font-size: 2rem;
  }
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
}

.slider-view__lightbox-prev {
  left: 1.5rem;
}

.slider-view__lightbox-next {
  right: 1.5rem;
}

.slider-view__lightbox-img {
  max-width: 90vw;
  max-height: 90vh;
  width: auto;
  height: auto;
  object-fit: contain;
}
</style>

<style lang="scss">
.slider-view__drag-ghost {
  img {
    image-rendering: auto;
    -webkit-backface-visibility: hidden;
    backface-visibility: hidden;
  }
}
</style>
